import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  Plus,
  Trash2,
  Edit,
  Save,
  X,
  GripVertical,
  Box,
  Building,
  CreditCard,
  FileText,
  Globe,
  Heart,
  Home,
  Mail,
  MapPin,
  Package,
  Phone,
  Shield,
  Star,
  Tag,
  Truck,
  User,
  Wallet,
  Briefcase,
  Calendar,
  Clock,
  Database,
  DollarSign,
  Flag,
  Gift,
  Key,
  Layers,
  Lock,
  Map,
  Percent,
  Receipt,
  RotateCcw,
  Settings2,
  RefreshCw,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { customElementApi, elementDefaultsApi } from "@/lib/api";
import { TypeEditor, SpacingEditor } from "@/components/designer/PropertiesPanel";
import {
  SECTION_DEFS,
  SECTION_ORDER,
  FONT_OPTIONS,
  CUSTOM_ICON_OPTIONS,
  FIELD_TYPES,
  ELEMENT_CATEGORIES,
  ELEMENT_TYPES,
} from "@/lib/elements";

const ICON_MAP = {
  Box, Building, CreditCard, FileText, Globe,
  Heart, Home, Mail, MapPin, Package,
  Phone, Shield, Star, Tag, Truck,
  User, Wallet, Briefcase, Calendar, Clock,
  Database, DollarSign, Flag, Gift, Key,
  Layers, Lock, Map, Percent, Receipt,
};

function getIcon(name) {
  return ICON_MAP[name] || Box;
}

const EMPTY_ELEMENT = {
  name: "",
  icon: "Box",
  description: "",
  fields: [],
  defaultSection: "body",
  style: {
    layout: "vertical",
    fontSize: 12,
    fontFamily: "Arial, sans-serif",
    color: "#374151",
    titleFontSize: 13,
    titleFontWeight: "bold",
    titleColor: "#111827",
  },
};

function FieldEditor({ field, index, onUpdate, onRemove }) {
  const update = (key, val) => onUpdate(index, { ...field, [key]: val });
  return (
    <div className="p-3 border rounded-lg bg-gray-50 space-y-2" data-testid={`field-editor-${index}`}>
      <div className="flex items-center justify-between">
        <span className="text-[10px] font-bold text-gray-400 uppercase">
          Field {index + 1}
        </span>
        <Button size="icon" variant="ghost" className="h-5 w-5" onClick={() => onRemove(index)}>
          <Trash2 className="w-3 h-3" />
        </Button>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="text-[10px] text-gray-500">Label</Label>
          <Input
            value={field.label || ""}
            onChange={(e) => update("label", e.target.value)}
            placeholder="Field Label"
            className="h-7 text-xs"
          />
        </div>
        <div>
          <Label className="text-[10px] text-gray-500">Key</Label>
          <Input
            value={field.key || ""}
            onChange={(e) => update("key", e.target.value)}
            placeholder="dataKey"
            className="h-7 text-xs font-mono"
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="text-[10px] text-gray-500">Type</Label>
          <Select value={field.type || "text"} onValueChange={(v) => update("type", v)}>
            <SelectTrigger className="h-7 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              {FIELD_TYPES.map((ft) => (
                <SelectItem key={ft.value} value={ft.value} className="text-xs">{ft.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] text-gray-500">Default Value</Label>
          <Input
            value={field.defaultValue || ""}
            onChange={(e) => update("defaultValue", e.target.value)}
            placeholder="Preview value"
            className="h-7 text-xs"
          />
        </div>
      </div>
      <div>
        <Label className="text-[10px] text-gray-500 flex items-center gap-1">
          EJS Binding <span className="font-mono text-blue-400">{"<%= ... %>"}</span>
        </Label>
        <Input
          value={field.ejsBinding || ""}
          onChange={(e) => update("ejsBinding", e.target.value)}
          placeholder="e.g. bank.name"
          className="h-7 text-xs font-mono"
        />
      </div>
    </div>
  );
}

function ElementPreview({ element }) {
  const s = element.style || {};
  const fields = element.fields || [];
  if (fields.length === 0) return <p className="text-xs text-gray-300 italic">Add fields to see preview</p>;

  const layout = s.layout || "vertical";

  return (
    <div
      style={{
        fontFamily: s.fontFamily || "Arial, sans-serif",
        fontSize: s.fontSize || 12,
        color: s.color || "#374151",
      }}
    >
      {element.name && (
        <p style={{
          fontSize: s.titleFontSize || 13,
          fontWeight: s.titleFontWeight || "bold",
          color: s.titleColor || "#111827",
          margin: "0 0 6px 0",
        }}>
          {element.name}
        </p>
      )}
      {layout === "vertical" && fields.map((f, i) => (
        <p key={i} style={{ margin: "2px 0", fontSize: s.fontSize || 12, color: s.color || "#374151" }}>
          {f.label && <strong>{f.label}: </strong>}
          <span style={{ color: "#9CA3AF" }}>{f.defaultValue || f.key || "..."}</span>
        </p>
      ))}
      {layout === "horizontal" && (
        <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
          {fields.map((f, i) => (
            <span key={i} style={{ fontSize: s.fontSize || 12 }}>
              {f.label && <strong>{f.label}: </strong>}
              <span style={{ color: "#9CA3AF" }}>{f.defaultValue || f.key || "..."}</span>
            </span>
          ))}
        </div>
      )}
      {layout === "grid" && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "2px 16px" }}>
          {fields.map((f, i) => (
            <p key={i} style={{ margin: "2px 0", fontSize: s.fontSize || 12 }}>
              {f.label && <strong>{f.label}: </strong>}
              <span style={{ color: "#9CA3AF" }}>{f.defaultValue || f.key || "..."}</span>
            </p>
          ))}
        </div>
      )}
    </div>
  );
}

function ElementForm({ initial, onSave, onCancel, saving }) {
  // Merge initial with defaults to ensure all required fields exist
  const [form, setForm] = useState(() => ({
    ...EMPTY_ELEMENT,
    ...initial,
    fields: initial?.fields || [],
    style: { ...EMPTY_ELEMENT.style, ...(initial?.style || {}) },
  }));
  const fields = form.fields || [];
  const style = form.style || {};

  const updateStyle = (key, val) =>
    setForm((f) => ({ ...f, style: { ...f.style, [key]: val } }));

  const addField = () => {
    setForm((f) => ({
      ...f,
      fields: [
        ...(f.fields || []),
        { label: "", key: "", type: "text", defaultValue: "", ejsBinding: "" },
      ],
    }));
  };

  const updateField = (i, updated) => {
    setForm((f) => ({
      ...f,
      fields: (f.fields || []).map((fld, idx) => (idx === i ? updated : fld)),
    }));
  };

  const removeField = (i) => {
    setForm((f) => ({
      ...f,
      fields: (f.fields || []).filter((_, idx) => idx !== i),
    }));
  };

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="font-heading font-bold text-lg text-gray-900">
          {initial?.id ? "Edit Element" : "Create Custom Element"}
        </h3>
        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onCancel}>
          <X className="w-4 h-4" />
        </Button>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label className="text-xs text-gray-500">Name</Label>
          <Input
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            placeholder="e.g. Bank Details"
            className="h-8 text-sm"
            data-testid="element-name-input"
          />
        </div>
        <div>
          <Label className="text-xs text-gray-500">Icon</Label>
          <Select value={form.icon} onValueChange={(v) => setForm((f) => ({ ...f, icon: v }))}>
            <SelectTrigger className="h-8 text-xs" data-testid="element-icon-select">
              <div className="flex items-center gap-2">
                {(() => { const Ic = getIcon(form.icon); return <Ic className="w-3.5 h-3.5" />; })()}
                <SelectValue />
              </div>
            </SelectTrigger>
            <SelectContent className="max-h-48">
              {CUSTOM_ICON_OPTIONS.map((name) => {
                const Ic = getIcon(name);
                return (
                  <SelectItem key={name} value={name} className="text-xs">
                    <div className="flex items-center gap-2">
                      <Ic className="w-3.5 h-3.5" /> {name}
                    </div>
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div>
        <Label className="text-xs text-gray-500">Description</Label>
        <Input
          value={form.description}
          onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
          placeholder="Brief description of this element"
          className="h-8 text-sm"
        />
      </div>

      <div>
        <Label className="text-xs text-gray-500">Default Section</Label>
        <Select value={form.defaultSection} onValueChange={(v) => setForm((f) => ({ ...f, defaultSection: v }))}>
          <SelectTrigger className="h-8 text-xs" data-testid="element-section-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {SECTION_ORDER.map((key) => (
              <SelectItem key={key} value={key} className="text-xs">
                <div className="flex items-center gap-1.5">
                  <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: SECTION_DEFS[key].color }} />
                  {SECTION_DEFS[key].label}
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <Separator />

      <div>
        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-3">
          Fields
        </p>
        <div className="space-y-2">
          {fields.map((f, i) => (
            <FieldEditor key={i} field={f} index={i} onUpdate={updateField} onRemove={removeField} />
          ))}
        </div>
        <Button
          variant="outline"
          size="sm"
          className="w-full mt-3 h-8 text-xs"
          onClick={addField}
          data-testid="add-field-btn"
        >
          <Plus className="w-3 h-3 mr-1" /> Add Field
        </Button>
      </div>

      <Separator />

      <div>
        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-3">
          Appearance
        </p>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <Label className="text-[10px] text-gray-500">Layout</Label>
            <Select value={style.layout || "vertical"} onValueChange={(v) => updateStyle("layout", v)}>
              <SelectTrigger className="h-7 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="vertical" className="text-xs">Vertical</SelectItem>
                <SelectItem value="horizontal" className="text-xs">Horizontal</SelectItem>
                <SelectItem value="grid" className="text-xs">Grid (2-col)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] text-gray-500">Font Size</Label>
            <Input type="number" value={style.fontSize || 12} onChange={(e) => updateStyle("fontSize", Number(e.target.value))} className="h-7 text-xs" min={8} max={20} />
          </div>
          <div>
            <Label className="text-[10px] text-gray-500">Font</Label>
            <Select value={style.fontFamily || "Arial, sans-serif"} onValueChange={(v) => updateStyle("fontFamily", v)}>
              <SelectTrigger className="h-7 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                {FONT_OPTIONS.map((f) => <SelectItem key={f} value={f} className="text-xs">{f.split(",")[0]}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-3 mt-2">
          <div>
            <Label className="text-[10px] text-gray-500">Text Color</Label>
            <div className="flex gap-1">
              <input type="color" value={style.color || "#374151"} onChange={(e) => updateStyle("color", e.target.value)} className="w-7 h-7 rounded border border-gray-200 cursor-pointer p-0.5" />
              <Input value={style.color || "#374151"} onChange={(e) => updateStyle("color", e.target.value)} className="h-7 text-xs font-mono flex-1" />
            </div>
          </div>
          <div>
            <Label className="text-[10px] text-gray-500">Title Size</Label>
            <Input type="number" value={style.titleFontSize || 13} onChange={(e) => updateStyle("titleFontSize", Number(e.target.value))} className="h-7 text-xs" />
          </div>
          <div>
            <Label className="text-[10px] text-gray-500">Title Color</Label>
            <div className="flex gap-1">
              <input type="color" value={style.titleColor || "#111827"} onChange={(e) => updateStyle("titleColor", e.target.value)} className="w-7 h-7 rounded border border-gray-200 cursor-pointer p-0.5" />
              <Input value={style.titleColor || "#111827"} onChange={(e) => updateStyle("titleColor", e.target.value)} className="h-7 text-xs font-mono flex-1" />
            </div>
          </div>
        </div>
      </div>

      <Separator />

      <div>
        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-3">
          Preview
        </p>
        <div className="p-4 border border-gray-200 rounded-lg bg-white">
          <ElementPreview element={form} />
        </div>
      </div>

      <div className="flex justify-end gap-2 pt-2">
        <Button variant="outline" onClick={onCancel} className="h-9">
          Cancel
        </Button>
        <Button
          onClick={() => onSave(form)}
          disabled={!form.name || saving}
          className="h-9 gap-2"
          data-testid="save-element-btn"
        >
          <Save className="w-3.5 h-3.5" />
          {saving ? "Saving..." : "Save Element"}
        </Button>
      </div>
    </div>
  );
}

/* ── Built-in Elements Tab ── */

function BuiltinElementsTab() {
  const [savedDefaults, setSavedDefaults] = useState({});
  const [selectedType, setSelectedType] = useState(null);
  const [editingProps, setEditingProps] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [applyConfirm, setApplyConfirm] = useState(false);
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    elementDefaultsApi
      .list()
      .then((items) => {
        const map = {};
        items.forEach((item) => {
          map[item.type] = item.overrides;
        });
        setSavedDefaults(map);
      })
      .catch(() => toast.error("Failed to load element defaults"))
      .finally(() => setLoading(false));
  }, []);

  const handleSelectType = (type) => {
    const baseProps = JSON.parse(JSON.stringify(ELEMENT_TYPES[type].defaultProps));
    const overrides = savedDefaults[type] || {};
    setSelectedType(type);
    setEditingProps({ ...baseProps, ...overrides });
  };

  const handleUpdate = (newProps) => {
    setEditingProps((prev) => ({ ...prev, ...newProps }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await elementDefaultsApi.save(selectedType, editingProps);
      setSavedDefaults((prev) => ({ ...prev, [selectedType]: editingProps }));
      toast.success(`Defaults saved for ${ELEMENT_TYPES[selectedType].label}`);
    } catch {
      toast.error("Failed to save defaults");
    }
    setSaving(false);
  };

  const handleReset = async () => {
    try {
      await elementDefaultsApi.reset(selectedType);
      setSavedDefaults((prev) => {
        const next = { ...prev };
        delete next[selectedType];
        return next;
      });
      setEditingProps(
        JSON.parse(JSON.stringify(ELEMENT_TYPES[selectedType].defaultProps))
      );
      setApplyConfirm(false);
      toast.success(`Defaults reset for ${ELEMENT_TYPES[selectedType].label}`);
    } catch {
      toast.error("Failed to reset defaults");
    }
  };

  const handleApplyToTemplates = async () => {
    setApplying(true);
    try {
      const result = await elementDefaultsApi.applyToTemplates(selectedType);
      toast.success(
        `Updated ${result.elements_updated} element(s) across ${result.templates_updated} template(s)`
      );
      setApplyConfirm(false);
    } catch {
      toast.error("Failed to apply defaults to templates");
    }
    setApplying(false);
  };

  if (loading) {
    return <div className="text-center py-12 text-gray-400">Loading...</div>;
  }

  if (selectedType) {
    const def = ELEMENT_TYPES[selectedType];
    const hasCustomDefaults = !!savedDefaults[selectedType];
    const editingEl = { type: selectedType, props: editingProps };

    return (
      <div
        className="bg-white border border-gray-200 rounded-xl overflow-hidden"
        data-testid="builtin-editor-panel"
      >
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => setSelectedType(null)}
              data-testid="builtin-editor-back-btn"
            >
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div>
              <h3 className="font-heading font-bold text-sm text-gray-900">
                {def.label} — Default Properties
              </h3>
              <p className="text-[10px] text-gray-400">{def.description}</p>
            </div>
          </div>
          <div className="flex gap-2">
            {hasCustomDefaults && (
              <Button
                variant="outline"
                size="sm"
                className="h-8 text-xs gap-1"
                onClick={handleReset}
                data-testid="builtin-reset-btn"
              >
                <RotateCcw className="w-3 h-3" /> Reset to Default
              </Button>
            )}
            <Button
              size="sm"
              className="h-8 gap-1"
              onClick={handleSave}
              disabled={saving}
              data-testid="builtin-save-btn"
            >
              <Save className="w-3.5 h-3.5" />
              {saving ? "Saving..." : "Save Defaults"}
            </Button>
          </div>
        </div>

        {applyConfirm ? (
          <div className="px-5 py-3 bg-amber-50 border-b border-amber-200 flex items-center justify-between gap-4">
            <p className="text-xs text-amber-700">
              This will overwrite existing <strong>{def.label}</strong> properties
              in all your saved templates. This cannot be undone.
            </p>
            <div className="flex gap-2 shrink-0">
              <Button
                variant="outline"
                size="sm"
                className="h-7 text-xs"
                onClick={() => setApplyConfirm(false)}
                data-testid="apply-confirm-cancel-btn"
              >
                Cancel
              </Button>
              <Button
                size="sm"
                className="h-7 text-xs bg-amber-600 hover:bg-amber-700 text-white"
                onClick={handleApplyToTemplates}
                disabled={applying}
                data-testid="apply-confirm-ok-btn"
              >
                {applying ? "Applying..." : "Apply"}
              </Button>
            </div>
          </div>
        ) : hasCustomDefaults ? (
          <div className="px-5 py-2 bg-blue-50 border-b border-blue-100 flex items-center justify-between gap-3">
            <p className="text-[10px] text-blue-600 font-medium">
              Custom defaults active — new elements use these settings
            </p>
            <Button
              variant="outline"
              size="sm"
              className="h-6 text-[10px] gap-1 text-blue-600 border-blue-200 shrink-0"
              onClick={() => setApplyConfirm(true)}
              data-testid="apply-to-existing-btn"
            >
              <RefreshCw className="w-3 h-3" /> Apply to Existing Templates
            </Button>
          </div>
        ) : null}

        <ScrollArea className="h-[calc(100vh-320px)]">
          <div className="p-5 space-y-3">
            <TypeEditor element={editingEl} onUpdate={handleUpdate} />
            <Separator className="my-3" />
            <SpacingEditor props={editingProps} onUpdate={handleUpdate} />
          </div>
        </ScrollArea>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="builtin-elements-grid">
      <p className="text-xs text-gray-500">
        Set default properties for built-in elements. New instances dragged onto the canvas
        will use these defaults.
      </p>
      {ELEMENT_CATEGORIES.map((category) => (
        <div key={category.name}>
          <h3 className="font-heading font-bold text-[10px] text-gray-400 uppercase tracking-widest mb-3">
            {category.name}
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {category.types.map((type) => {
              const def = ELEMENT_TYPES[type];
              const hasOverrides = !!savedDefaults[type];
              return (
                <Card
                  key={type}
                  className={`cursor-pointer hover:shadow-md transition-all ${
                    hasOverrides
                      ? "border-blue-300 bg-blue-50/30"
                      : "border-gray-200"
                  }`}
                  onClick={() => handleSelectType(type)}
                  data-testid={`builtin-element-card-${type}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-heading font-bold text-gray-900 truncate">
                        {def.label}
                      </span>
                      {hasOverrides && (
                        <span className="text-[9px] bg-blue-100 text-blue-600 px-1.5 py-0.5 rounded font-semibold shrink-0 ml-1">
                          Custom
                        </span>
                      )}
                    </div>
                    <p className="text-[10px] text-gray-400 truncate">
                      {def.description}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function ConfigurePage() {
  const navigate = useNavigate();
  const [elements, setElements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("custom");

  const fetchElements = () => {
    customElementApi
      .list()
      .then(setElements)
      .catch(() => toast.error("Failed to load custom elements"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchElements();
  }, []);

  const handleSave = async (form) => {
    setSaving(true);
    try {
      if (editing?.id) {
        await customElementApi.update(editing.id, form);
        toast.success("Element updated");
      } else {
        await customElementApi.create(form);
        toast.success("Element created");
      }
      setEditing(null);
      fetchElements();
    } catch {
      toast.error("Failed to save element");
    }
    setSaving(false);
  };

  const handleDelete = async (el) => {
    try {
      await customElementApi.delete(el.id);
      setElements((prev) => prev.filter((e) => e.id !== el.id));
      toast.success("Element deleted");
    } catch {
      toast.error("Failed to delete element");
    }
  };

  return (
    <div className="min-h-screen bg-[#F9FAFB]" data-testid="configure-page">
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-6 py-8">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => navigate("/")} data-testid="config-back-btn">
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div className="flex-1">
              <h1 className="font-heading font-bold text-2xl text-gray-900">
                Configure
              </h1>
              <p className="text-xs text-gray-500 mt-1">
                Manage custom elements and set defaults for built-in elements
              </p>
            </div>
            {!editing && activeTab === "custom" && (
              <Button
                onClick={() => setEditing({})}
                className="gap-2 h-9"
                data-testid="create-element-btn"
              >
                <Plus className="w-4 h-4" /> Create Element
              </Button>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8 space-y-6">
        <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); setEditing(null); }}>
          <TabsList className="h-9 bg-gray-100 p-1 w-auto">
            <TabsTrigger value="custom" className="text-xs gap-1.5 data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="tab-custom-elements">
              <Box className="w-3 h-3" /> Custom Elements
            </TabsTrigger>
            <TabsTrigger value="builtin" className="text-xs gap-1.5 data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="tab-builtin-elements">
              <Settings2 className="w-3 h-3" /> Built-in Defaults
            </TabsTrigger>
          </TabsList>

          <TabsContent value="custom" className="mt-6">
            {editing !== null && (
              <ElementForm
                initial={editing}
                onSave={handleSave}
                onCancel={() => setEditing(null)}
                saving={saving}
              />
            )}

            {!editing && (
              <>
                {loading ? (
                  <div className="text-center py-12 text-gray-400">Loading...</div>
                ) : elements.length === 0 ? (
                  <div className="text-center py-16 border-2 border-dashed border-gray-200 rounded-lg">
                    <Box className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                    <p className="text-sm text-gray-400 mb-1">No custom elements yet</p>
                    <p className="text-xs text-gray-300 mb-4">
                      Create one to use across all your invoice templates
                    </p>
                    <Button
                      variant="outline"
                      onClick={() => setEditing({})}
                      className="gap-2"
                      data-testid="create-first-element-btn"
                    >
                      <Plus className="w-4 h-4" /> Create Your First Element
                    </Button>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {elements.map((el) => {
                      const Ic = getIcon(el.icon);
                      const secDef = SECTION_DEFS[el.defaultSection];
                      return (
                        <Card
                          key={el.id}
                          className="group hover:shadow-md transition-shadow border-gray-200"
                          data-testid={`custom-element-card-${el.id}`}
                        >
                          <CardContent className="p-5">
                            <div className="flex items-start gap-3 mb-3">
                              <div className="w-9 h-9 rounded-lg bg-gray-100 flex items-center justify-center shrink-0">
                                <Ic className="w-4 h-4 text-gray-500" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <h3 className="font-heading font-bold text-sm text-gray-900 truncate">
                                  {el.name}
                                </h3>
                                {el.description && (
                                  <p className="text-[10px] text-gray-400 mt-0.5 truncate">
                                    {el.description}
                                  </p>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-2 mb-3">
                              <span className="text-[10px] text-gray-400">
                                {(el.fields || []).length} fields
                              </span>
                              <span className="text-[10px] text-gray-300">&middot;</span>
                              <div className="flex items-center gap-1">
                                <div
                                  className="w-1.5 h-1.5 rounded-full"
                                  style={{ backgroundColor: secDef?.color || "#666" }}
                                />
                                <span className="text-[10px] text-gray-400">
                                  {secDef?.label || el.defaultSection}
                                </span>
                              </div>
                            </div>
                            <div className="p-2 bg-gray-50 rounded border border-gray-100 mb-3">
                              <ElementPreview element={el} />
                            </div>
                            <div className="flex gap-1 justify-end">
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 text-xs gap-1"
                                onClick={() => setEditing(el)}
                                data-testid={`edit-element-${el.id}`}
                              >
                                <Edit className="w-3 h-3" /> Edit
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 text-xs gap-1 text-red-500 hover:text-red-600"
                                onClick={() => handleDelete(el)}
                                data-testid={`delete-element-${el.id}`}
                              >
                                <Trash2 className="w-3 h-3" /> Delete
                              </Button>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </TabsContent>

          <TabsContent value="builtin" className="mt-6">
            <BuiltinElementsTab />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
